import { Button, Checkbox, Input, Modal, Upload } from 'antd';
import * as React from 'react';
import { useState } from 'react';
import { CloseOutlined, DownOutlined, SearchOutlined, UploadOutlined, UpOutlined } from '@ant-design/icons';
import './style.css';

export interface PublisherDomainListCardProps {
    title: string
    subtitle: string
    items: string[]
    acceptAll: boolean
    collapsed: boolean
    onToggleCollapse: () => void
    onAcceptAllChange: (val: boolean) => void
    onAdd: (value: string) => void
    onUpload: (file: File) => void
    onRemoveItem: (index: number) => void
    onClearAll: () => void
    initialFileLabel?: string | null
}

const DOMAIN_REGEX = /^(?!-)[A-Za-z0-9-]{1,63}(?<!-)(\.(?!-)[A-Za-z0-9-]{1,63}(?<!-))*\.[A-Za-z]{2,63}$/

const extractDomain = (value: string) =>
    value
        .trim()
        .replace(/^(https?:\/\/)?(www\.)?/i, '')
        .replace(/[/?#].*$/, '')
        .replace(/\/$/, '')

const isValidDomain = (value: string) => DOMAIN_REGEX.test(extractDomain(value))

interface RemovableItemListProps {
    entries: { item: string; idx: number }[]
    onRemove: (index: number) => void
    disabled?: boolean
    emptyText?: string
}

const BATCH_SIZE = 100

const RemovableItemList: React.FC<RemovableItemListProps> = ({
    entries, onRemove, disabled = false, emptyText = 'No items yet',
}) => {
    const [visibleCount, setVisibleCount] = useState(BATCH_SIZE)
    const scrollRef = React.useRef<HTMLDivElement>(null)

    React.useEffect(() => { setVisibleCount(BATCH_SIZE) }, [entries.length])

    const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
        const el = e.currentTarget
        if (el.scrollTop + el.clientHeight >= el.scrollHeight - 100) {
            setVisibleCount(prev => Math.min(prev + BATCH_SIZE, entries.length))
        }
    }

    if (entries.length === 0) {
        return (
            <div className="ril-empty">
                <span className="ril-empty-text">{emptyText}</span>
            </div>
        )
    }

    const visibleItems = entries.slice(0, visibleCount)

    return (
        <div ref={scrollRef} onScroll={handleScroll} className="removable-item-list">
            {visibleItems.map(({ item, idx }) => (
                <div key={item} className="ril-chip" title={item}>
                    <button
                        type="button"
                        className="ril-remove"
                        disabled={disabled}
                        aria-label={`Remove ${item}`}
                        onClick={() => { if (!disabled) onRemove(idx) }}
                    >
                        <CloseOutlined />
                    </button>
                    <span className="ril-label">{item}</span>
                </div>
            ))}
            {visibleCount < entries.length && (
                <div className="ril-more">
                    Showing {visibleCount} of {entries.length} — scroll to load more
                </div>
            )}
        </div>
    )
}

const PublisherDomainListCard: React.FC<PublisherDomainListCardProps> = ({
    title, subtitle, items, acceptAll,
    collapsed, onToggleCollapse,
    onAcceptAllChange,
    onAdd, onUpload, onRemoveItem, onClearAll,
    initialFileLabel,
}) => {
    const [inputValue, setInputValue] = useState('')
    const [validationMsg, setValidationMsg] = useState('')
    const [search, setSearch] = useState('')
    const [fileLabel, setFileLabel] = useState<string | null>(initialFileLabel ?? null)
    const [showProcessingModal, setShowProcessingModal] = useState(false)
    const [showReviewModal, setShowReviewModal] = useState(false)
    const [parsedItems, setParsedItems] = useState<string[]>([])
    const [invalidCount, setInvalidCount] = useState(0)
    const [csvFileName, setCsvFileName] = useState('')
    const [progress, setProgress] = useState(0)

    const isDomainsCard = title === 'Web Domains'

    React.useEffect(() => {
        if (initialFileLabel !== undefined) setFileLabel(initialFileLabel ?? null)
    }, [initialFileLabel])

    React.useEffect(() => {
        if (items.length === 0) setFileLabel(null)
    }, [items.length])

    const filtered = items
        .map((item, idx) => ({ item, idx }))
        .filter(({ item }) => item.toLowerCase().includes(search.toLowerCase()))

    const handleAdd = () => {
        const trimmed = inputValue.trim()
        if (!trimmed) return
        if (isDomainsCard && !isValidDomain(trimmed)) {
            setValidationMsg('Please enter a valid domain name.')
            return
        }
        const value = isDomainsCard ? extractDomain(trimmed) : trimmed
        if (items.some(d => d.toLowerCase() === value.toLowerCase())) {
            setValidationMsg(`"${value}" has already been added.`)
            return
        }
        setValidationMsg('')
        onAdd(value)
        setInputValue('')
    }

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') { e.preventDefault(); handleAdd() }
    }

    const handleFileUpload = (file: File) => {
        setCsvFileName(file.name)
        setProgress(0)
        setShowProcessingModal(true)
        const reader = new FileReader()
        reader.onloadend = () => {
            let prog = 0
            const interval = setInterval(() => {
                prog += 20
                setProgress(prog)
                if (prog >= 100) {
                    clearInterval(interval)
                    const lines = (reader.result as string)
                        .split('\n').map(l => l.trim()).filter(Boolean)
                    if (isDomainsCard) {
                        const valid = lines.filter(isValidDomain)
                        setParsedItems(valid)
                        setInvalidCount(lines.length - valid.length)
                    } else {
                        setParsedItems(lines)
                        setInvalidCount(0)
                    }
                    setShowProcessingModal(false)
                    setShowReviewModal(true)
                }
            }, 80)
        }
        reader.readAsText(file)
        return false
    }

    const handleConfirmImport = () => {
        const existingLower = new Set(items.map(d => d.toLowerCase()))
        const unique = parsedItems
            .filter(d => !isDomainsCard || isValidDomain(d))
            .map(d => (isDomainsCard ? extractDomain(d) : d))
            .filter(d => !existingLower.has(d.toLowerCase()))
        unique.forEach(item => onAdd(item))
        setFileLabel(isDomainsCard ? 'domains.csv' : 'mobileAppIds.csv')
        setShowReviewModal(false)
        setParsedItems([])
        setInvalidCount(0)
    }

    const handleRemoveParsedItem = (idx: number) => {
        setParsedItems(prev => prev.filter((_, i) => i !== idx))
    }

    const handleDownload = () => {
        if (items.length === 0) return
        const csvContent = items.join('\n')
        const blob = new Blob([csvContent], { type: 'text/csv' })
        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = isDomainsCard ? 'domains.csv' : 'mobileAppIds.csv'
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)
        URL.revokeObjectURL(url)
    }

    const handleClearAll = () => {
        Modal.confirm({
            title: `Clear all ${isDomainsCard ? 'domains' : 'mobile app IDs'}?`,
            content: 'This cannot be undone.',
            okText: 'Clear All',
            okButtonProps: { danger: true },
            cancelText: 'Cancel',
            onOk: () => { onClearAll(); setFileLabel(null) },
        })
    }

    return (
        <div className="pdlc-card">
            <div className="pdlc-header">
                <div>
                    <div className="pdlc-title">{title}</div>
                    <div className="pdlc-subtitle">{subtitle}</div>
                </div>
                <button type="button" className="pdlc-collapse-btn" onClick={onToggleCollapse}>
                    {collapsed ? <DownOutlined /> : <UpOutlined />}
                </button>
            </div>

            {!collapsed && (
                <div className="pdlc-body">
                    <div className="pdlc-add-label">
                        Add {isDomainsCard ? 'Domains' : 'Mobile App IDs'}
                    </div>
                    <div className="pdlc-input-row">
                        <Input
                            className="pdlc-add-input"
                            value={inputValue}
                            onChange={e => { setInputValue(e.target.value); setValidationMsg('') }}
                            onKeyDown={handleKeyDown}
                            placeholder={isDomainsCard ? 'Enter domain' : 'Enter mobile app ID'}
                            disabled={acceptAll}
                        />
                        <Button className="pdlc-add-btn" type="primary" onClick={handleAdd} disabled={acceptAll}>Add</Button>
                        <Upload accept=".csv,.txt" beforeUpload={handleFileUpload} showUploadList={false}>
                            <Button className="pdlc-upload-btn" icon={<UploadOutlined />} disabled={acceptAll}>Upload</Button>
                        </Upload>
                    </div>
                    <div className="pdlc-meta-row">
                        <span className="pdlc-error">{validationMsg}</span>
                        {fileLabel && (
                            <span className="pdlc-file">
                                <span
                                    className="pdlc-file-name"
                                    onClick={handleDownload}
                                    title="Download uploaded CSV"
                                >
                                    {fileLabel}
                                </span>{' '}
                                <span className="pdlc-file-remove" onClick={handleClearAll}>×</span>
                            </span>
                        )}
                    </div>

                    <div className="pdlc-acceptall-row">
                        <Checkbox checked={acceptAll} onChange={e => onAcceptAllChange(e.target.checked)}>All</Checkbox>
                    </div>

                    {acceptAll ? (
                        <div className="pdlc-disabled-box">
                            <div className="pdlc-disabled-title">All Option is Enabled</div>
                            <div className="pdlc-disabled-sub">
                                Uncheck 'All' to access {isDomainsCard ? 'domain' : 'mobile app ID'} List.
                            </div>
                        </div>
                    ) : (
                        <>
                            <div className="pdlc-list-header">
                                <span className="pdlc-list-title">
                                    {isDomainsCard ? 'Domains' : 'Mobile App IDs'} List{' '}
                                    <span className="pdlc-count">{items.length}</span>
                                </span>
                                <div>
                                    <Button size="small" onClick={handleClearAll}>Clear All</Button>
                                </div>
                            </div>
                            <Input
                                className="pdlc-search"
                                prefix={<SearchOutlined className="pdlc-search-icon" />}
                                placeholder={`Search ${isDomainsCard ? 'Domains' : 'Mobile App IDs'}`}
                                value={search}
                                onChange={e => setSearch(e.target.value)}
                            />
                            <RemovableItemList
                                entries={filtered}
                                onRemove={onRemoveItem}
                            />
                        </>
                    )}
                </div>
            )}

            <Modal open={showProcessingModal} footer={null} closable={false} centered width={360}>
                <div className="pdlc-modal-processing">
                    <div className="pdlc-modal-processing-title">Processing {csvFileName}</div>
                    <div className="pdlc-modal-processing-sub">Parsing {isDomainsCard ? 'Domains' : 'Mobile App IDs'}</div>
                    <div className="pdlc-progress-track">
                        <div className="pdlc-progress-bar" style={{ width: `${progress}%` }} />
                    </div>
                </div>
            </Modal>

            <Modal open={showReviewModal} title="Review CSV import" footer={null} onCancel={() => { setShowReviewModal(false); setParsedItems([]); setInvalidCount(0) }} centered width={480}>
                <p className="pdlc-review-text">
                    {parsedItems.length} {isDomainsCard ? 'domains' : 'mobile app IDs'} found in {csvFileName}. These will be added to your {isDomainsCard ? 'domains' : 'mobile app IDs'} list.
                </p>
                {isDomainsCard && invalidCount > 0 && (
                    <p className="pdlc-review-invalid">
                        {invalidCount} invalid domain{invalidCount > 1 ? 's' : ''} {invalidCount > 1 ? 'were' : 'was'} skipped.
                    </p>
                )}
                <div className="pdlc-review-list">
                    {parsedItems.map((item, idx) => (
                        <span key={idx} className="pdlc-review-chip">
                            {item}
                            <span className="pdlc-review-chip-remove" onClick={() => handleRemoveParsedItem(idx)}>×</span>
                        </span>
                    ))}
                    {parsedItems.length === 0 && <span className="pdlc-review-empty">No items</span>}
                </div>
                <div className="pdlc-review-actions">
                    <Button onClick={() => { setShowReviewModal(false); setParsedItems([]); setInvalidCount(0) }}>Cancel</Button>
                    <Button type="primary" disabled={parsedItems.length === 0} onClick={handleConfirmImport}>
                        Add {parsedItems.length} {isDomainsCard ? 'Domains' : 'Mobile App IDs'}
                    </Button>
                </div>
            </Modal>
        </div>
    )
}

export default PublisherDomainListCard;
